import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { DateValue } from '@internationalized/date';
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  DateInput,
  DatePicker,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Popover,
} from 'react-aria-components';

import {
  isSameDateValue,
  mergeClassNameStrings,
  today,
} from '@consumer-frontend/nc-utils';
import { Icon } from '@consumer-frontend/shared-ui';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/form/datepicker.scss';

export type NcDatePickerPropsT = {
  id: string;
  label: ReactNode;
  defaultValue?: DateValue;
  disabled?: boolean;
  disabledDates?: DateValue[];
  errorMessage?: ReactNode;
  granularity?: 'day' | 'month';
  helperText?: ReactNode;
  hideCalendar?: boolean;
  max?: DateValue;
  min?: DateValue;
  onBlur?: () => void;
  onChange?: (date: DateValue | null) => void;
  specialDates?: DateValue[];
};

export type NcDatePickerT = React.FC<NcDatePickerPropsT>;

// Without the offset, the date picker calendar will appear
// over the input instead of below it and will otherwise
// not align with the input as desired
const CALENDAR_VERTICAL_OFFSET = 10;
const CALENDAR_HORIZONTAL_OFFSET = 12;

export const NcDatePicker: NcDatePickerT = ({
  defaultValue = null,
  disabled,
  disabledDates = [],
  errorMessage,
  granularity = 'day',
  helperText,
  hideCalendar = false,
  id,
  label,
  max,
  min,
  onBlur,
  onChange,
  specialDates = [],
}) => {
  const [dateValue, setDateValue] = useState(defaultValue);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const classNames = mergeClassNameStrings('react-aria-DatePicker', errorMessage ? 'invalid-datepicker' : '');
  const popoverControlProps = hideCalendar
    ? {}
    : {
      isOpen: isPopoverOpen,
      onOpenChange: setIsPopoverOpen,
    };

  // Helpers
  const isDateDisabled = useCallback(
    (date: DateValue) => {
      if (min && date.compare(min) < 0) return true;
      if (max && date.compare(max) > 0) return true;

      return disabledDates.some((d) => isSameDateValue(date, d));
    },
    [
      disabledDates,
      max,
      min,
    ],
  );

  const onDateChange = (date: DateValue | null): void => {
    onChange?.(date);
    setDateValue(date);
    setIsPopoverOpen(false);
  };

  const nextAvailableDate = useMemo<DateValue>(
    () => {
      let date = min || today();

      while (isDateDisabled(date)) {
        date = date.add({ days: 1 });
      }

      return date;
    },
    [isDateDisabled, min],
  );

  // Sync internal state if defaultValue changes
  useEffect(
    () => setDateValue(defaultValue),
    [defaultValue],
  );

  return (
    <div className="nc-datepicker">
      {label && (
        <label
          className="datepicker-label form-label"
          htmlFor={id}
          id={`${id}-label`}
        >
          {label}
        </label>
      )}

      {helperText && <span id={`${id}-help`} className="d-block helper-text">{helperText}</span>}

      <DatePicker
        {...popoverControlProps}
        aria-labelledby={`${id}-label`}
        className={classNames}
        // @ts-expect-error - granularity type does not officially support 'month', but it does work.
        granularity={granularity}
        minValue={min}
        maxValue={max}
        id={id}
        isDateUnavailable={isDateDisabled}
        isDisabled={disabled}
        onBlur={onBlur}
        onChange={onDateChange}
        value={dateValue}
      >
        <Group>
          <DateInput>
            {(segment) => <DateSegment segment={segment} />}
          </DateInput>
          <span className="date-picker-icons">
            {errorMessage && <Icon iconName="exclamation-circle" className="date-picker-invalid-icon" />}
            {!hideCalendar && (
              <Button className="react-aria-Button calendar-button">
                <Icon iconName="calendar" className="calendar-icon" />
              </Button>
            )}
          </span>
        </Group>

        {!hideCalendar && (
          <Popover
            crossOffset={CALENDAR_HORIZONTAL_OFFSET}
            offset={CALENDAR_VERTICAL_OFFSET}
            placement="bottom right"
          >
            <Dialog>
              <Calendar>
                {/* Calendar Header with Navigation */}
                <div className="calendar-header">
                  <Button slot="previous">
                    <Icon iconName="chevron-left" className="calendar-navigation-icon" id="left-month-arrow" />
                  </Button>

                  <Heading />

                  <Button slot="next">
                    <Icon iconName="chevron-right" className="calendar-navigation-icon" id="right-month-arrow" />
                  </Button>
                </div>

                <CalendarGrid>
                  {(date) => (
                    <CalendarCell date={date}>
                      {specialDates.find((d) => isSameDateValue(date, d))
                      && (<>{date.day}<Icon iconName="dot" className="day-dot" /></>)}
                    </CalendarCell>
                  )}
                </CalendarGrid>

                <div className="next-available-date-container">
                  <button
                    className="next-available-date-button"
                    onClick={() => onDateChange(nextAvailableDate)}
                    type="button"
                  >
                    Next Available Date
                  </button>
                </div>
              </Calendar>
            </Dialog>
          </Popover>
        )}
      </DatePicker>

      {errorMessage && (
        <div id={`${id}-error`} role="alert" className="feedback invalid-feedback">
          {errorMessage}
        </div>
      )}
    </div>
  );
};
